import React from 'react';
import cx from 'classnames';
import { Button } from '@clearcover/atlas';
import { ButtonGroup, Modal } from 'Shared/components';
import { useLazyBindApi } from 'Shared/services/Api';
import { CreateDriverResponseType } from 'QuoteApp/services/endpoints/PostAddDriver';
import { useAppContext } from 'QuoteApp/services/providers';
import { getModalContent } from './Content';
import styles from './styles.module.css';

type DriverStatusModalProps = {
  driverId: string;
  driverLabel: string;
};

export const DriverStatusModal = ({
  driverId,
  driverLabel,
}: DriverStatusModalProps) => {
  const {
    appId,
    details: { policyHolders, productType },
    detailsLoading,
    refreshDetails,
  } = useAppContext();

  const { policyHolderRequiredType, policyHolderType } =
    policyHolders?.find(({ identifier, commands }) => {
      return (
        identifier === driverId &&
        commands?.some(
          ({ name, runnable }) =>
            runnable && name === 'confirm_policy_holder_type_change'
        )
      );
    }) || {};

  const showModal =
    policyHolderRequiredType === 'additionalInsured' ||
    policyHolderRequiredType === 'excludedPerson' ||
    policyHolderRequiredType === 'listOnly' ||
    policyHolderRequiredType === 'removed';

  const [{ loading }, changePolicyHolderType] = useLazyBindApi<
    CreateDriverResponseType
  >({
    method: 'POST',
    url: `/insurance_applications/${appId}/insurance_products/${productType}/policy_holders/${driverId}/confirm_policy_holder_type_change`,
  });

  if (!showModal) return null;

  const { title, contents, buttonName } = getModalContent(
    driverLabel,
    policyHolderType?.value
  )[policyHolderRequiredType];

  const handleModalButtonClick = () => {
    changePolicyHolderType().then(async () => {
      // TODO CHANGE ( use response with setDetails fnx )
      await refreshDetails();
    });
  };

  return (
    <Modal showModal disableCurtainClick>
      <div className={styles.modalContainer}>
        <div className={styles.modalBody}>
          <span className={styles.modalTitle}>{title}</span>
          {contents.map(({ text, className }, index) => (
            <div key={index} className={cx(styles.modalContent, className)}>
              <span dangerouslySetInnerHTML={{ __html: text }} />
            </div>
          ))}
        </div>
        <ButtonGroup alignRight>
          <Button
            type="submit"
            variant="primary"
            disabled={loading || detailsLoading}
            isSubmitting={loading || detailsLoading}
            data-testid="modal-prompt-continue"
            onClick={handleModalButtonClick}
          >
            {buttonName}
          </Button>
        </ButtonGroup>
      </div>
    </Modal>
  );
};

export default DriverStatusModal;
